import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification, sanitizeHtml } from "@repo/helpers";
import { BasicDialog, Button, ButtonTypes, Loading } from "@repo/ui";
import { useCallback, useMemo, useState } from "react";
import HtmlParser from "react-html-parser";

import type { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import type { EnrollmentValidationError } from "../../../models";

import { useEnrollmentValidationErrorApi } from "../../../api/eveAPI";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { useEVEContext, useRepContext } from "../../../contexts/editableItemContext";
import { tabs } from "../../../string";
import { useIncidentLists } from "../../repIncident/incidentLists";
import { RepDisciplineEditorModal } from "./repDisciplineEditor";
import style from "./style.module.scss";

interface IState extends ITemplateData {
	incident: EnrollmentValidationError;
}

function EVEEditor() {
	const context = useEVEContext();
	const { rep, repId } = useRepContext();
	const { item: incident, updateItem, updateSavedItem } = context;
	const [incidentLists, allLoaded] = useIncidentLists();
	const {
		saveEnrollmentValidationError,
		loadEnrollmentValidationError,
		assignToAnalyst,
		createIncident,
		useEVEsQuery,
	} = useEnrollmentValidationErrorApi();
	const { clearQuery } = useEVEsQuery(repId);
	const [showEmails, setShowEmails] = useState(false);
	const [showAllEmails, setShowAllEmails] = useState(false);
	const [showAddDiscipline, setShowAddDiscipline] = useState(false);
	const templateData = useMemo(
		() => ({
			incident,
			incidentLists,
		}),
		[incident, incidentLists],
	);
	const [representativeId] = useState<number>(null);

	const save = useCallback(
		async (email: boolean) => {
			const notify = PushProcessingNotification("Saving Validation Error");
			try {
				incident.representativeId = incident.representativeId ?? representativeId;
				incident.status = incident.statusId ?? incident.status;
				incident.loaStatus = incident.loaStatusId ?? incident.loaStatus;
				incident.customerContactType = incident.customerContactTypeId ?? incident.customerContactType;
				incident.customerCallAttempt = incident.customerCallAttemptId ?? incident.customerCallAttempt;
				const id = await saveEnrollmentValidationError(incident, email);
				notify.processing("Loading...");
				const savedIncident = loadEnrollmentValidationError(id);
				await clearQuery();
				notify.success("Saved!");
				return savedIncident;
			} catch (ex) {
				notify.error("Failed to save");
				throw ex;
			}
		},
		[incident],
	);

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		//Custom logic
		switch (id) {
			default:
				break;
		}

		return newState as IState;
	}

	async function onModalSave() {
		await save(false);
	}

	function customFunctions(name: string) {
		switch (name) {
			case "assignQaAnalyst":
				return assignQaAnalyst;
			case "addDiscipline":
				return addDiscipline;
			case "createCustomerIncident":
				return createCustomerIncident;
		}

		async function assignQaAnalyst() {
			const { id: eveId, assignedAnalystId } = incident;
			const analystName = await assignToAnalyst(eveId, assignedAnalystId);
			if (analystName) {
				updateItem({
					qaAnalystId: assignedAnalystId,
					qaAnalystName: analystName,
				});
				updateSavedItem({
					qaAnalystId: assignedAnalystId,
					qaAnalystName: analystName,
				});
			}
		}

		async function addDiscipline() {
			setShowAddDiscipline(true);
		}

		async function createCustomerIncident() {
			await save(false);
			const complaintId = await createIncident(incident.id);
			updateItem({ complaintId });
			updateSavedItem({ complaintId });
			await clearQuery();
		}
	}

	function renderEmailText() {
		const emailText = incident?.emailText;

		if (!emailText) return null;

		//Add <tbody> tag and remove extra whitespaces to prevent validateDOMNesting warnings
		const parsedEmailText = HtmlParser(
			sanitizeHtml(
				emailText
					.replace(/<table>\s*<tr>/gi, "<table><tbody><tr>")
					.replace(/<\/tr>\s*<\/table>/gi, "</tr></tbody></table>")
					.replace(/>\s*</gi, "><")
					.replace(/\s*</gi, "<")
					.replace(/>\s*/gi, ">"),
			),
		);

		return (
			<>
				{renderButtons()}
				{renderEmails()}
			</>
		);

		function renderButtons() {
			const showAllEmailsButton = showEmails && parsedEmailText.length > 1;
			return (
				<div className={style.buttonContainer}>
					<Button onClick={() => setShowEmails(!showEmails)} types={[ButtonTypes.Primary]}>
						{showEmails ? "Hide" : "Show"} Email Text
					</Button>
					{showAllEmailsButton && (
						<Button onClick={() => setShowAllEmails(!showAllEmails)} types={[ButtonTypes.Primary]}>
							Show {showAllEmails ? "Only Relevant" : "All"}
						</Button>
					)}
				</div>
			);
		}

		function renderEmails() {
			if (!showEmails) return null;
			if (showAllEmails) return parsedEmailText;
			return parsedEmailText.find((r) => String(r.props.id) === String(incident?.validationTypeId));
		}
	}

	if (!incident || !incident.id) return null;

	return (
		<Loading loading={!allLoaded}>
			{renderEmailText()}
			<EditableItemDynamicTab
				itemContext={context}
				pageName={tabs.eves}
				otherTemplateData={templateData}
				itemProp="incident"
				updateValues={updateValues}
				save={save}
				customFunctions={customFunctions}
				saveAndEmail
				loaded
				containerClass="mb-8"
			/>
			<BasicDialog
				title="Add Discipline"
				open={showAddDiscipline}
				onOpenChange={setShowAddDiscipline}
				hideTrigger
			>
				<RepDisciplineEditorModal
					rep={rep}
					incidentId={incident.id}
					onModalSave={onModalSave}
					onClose={() => setShowAddDiscipline(false)}
				/>
			</BasicDialog>
		</Loading>
	);
}

export default EVEEditor;
